/**
 * Substance shelf — two rows (liquids/solutions, solids), each reagent shown
 * as a bottle/jar icon with its formula (or short name) as the label. Click to
 * add to the selected container; hover for the full name when the label alone
 * doesn't say enough. Click-to-add rather than drag-and-drop for the Phase 3
 * functional-loop pass; see chemistry-lab-implplan.md Phase 3/4.
 *
 * A shelf wider than the bar gets ‹ › browse arrows rather than shrinking its
 * bottles — the reagents have to stay big enough to tell apart at a glance.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { SUBSTANCE_CATALOG, CATEGORY_LABELS, type Substance, type SubstanceCategory } from '../substanceCatalog';
import { SubstanceIcon } from './SubstanceIcon';
import { ChemFormula } from './ChemFormula';

interface SubstanceTrayProps {
  /** `at` is where on screen the reagent was clicked, so anything that has to
   *  ask a question before the pour (the quantitative bench's strength
   *  chooser) can open where the hand already is. Ignored by the free bench,
   *  which pours on the click. */
  onAdd: (substanceId: string, at?: { x: number; y: number }) => void;
  disabled: boolean;
}

function ShelfRow({ title, label, items, onAdd, disabled }: {
  title: React.ReactNode; label: string; items: Substance[];
  onAdd: (id: string, at?: { x: number; y: number }) => void; disabled: boolean;
}): React.JSX.Element {
  const rowRef = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState({ overflows: false, atStart: true, atEnd: false });

  const measure = useCallback((): void => {
    const el = rowRef.current;
    if (!el) return;
    // 1px slack: fractional layout widths otherwise leave "atEnd" permanently false.
    const overflows = el.scrollWidth - el.clientWidth > 1;
    setScroll({
      overflows,
      atStart: el.scrollLeft <= 1,
      atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1,
    });
  }, []);

  // The row's own width is what decides whether arrows are needed, so watch the
  // element rather than the window — the side panels and sidebar collapsing both
  // change it without a window resize.
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, items]);

  const browse = (direction: -1 | 1): void => {
    const el = rowRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(200, el.clientWidth * 0.8), behavior: 'smooth' });
  };

  return (
    <div className="chemlab-shelf">
      <span className="chemlab-shelf-title">{title}</span>
      {/* Always in the layout, even for a shelf that fits: an arrow that
          appears and disappears slides the whole row sideways. Hidden, not
          removed. */}
      <button
        className="chemlab-shelf-arrow"
        onClick={() => browse(-1)}
        disabled={!scroll.overflows || scroll.atStart}
        data-idle={!scroll.overflows || undefined}
        aria-hidden={!scroll.overflows || undefined}
        tabIndex={scroll.overflows ? undefined : -1}
        aria-label={`Scroll ${label} left`}
      >
        ‹
      </button>
      <div className="chemlab-shelf-row" ref={rowRef} onScroll={measure}>
        {items.map(s => (
          <button
            key={s.id}
            data-substance-id={s.id}
            className="chemlab-shelf-item"
            disabled={disabled}
            onClick={e => onAdd(s.id, { x: e.clientX, y: e.clientY })}
            title={s.formula ? `${s.displayName} (${s.formula})` : s.displayName}
          >
            <SubstanceIcon physicalState={s.physicalState} color={s.color} form={s.form} unit={s.dose?.unit} />
            <span className="chemlab-shelf-label">
              <ChemFormula formula={s.formula} fallback={s.displayName} />
            </span>
          </button>
        ))}
      </div>
      <button
        className="chemlab-shelf-arrow"
        onClick={() => browse(1)}
        disabled={!scroll.overflows || scroll.atEnd}
        data-idle={!scroll.overflows || undefined}
        aria-hidden={!scroll.overflows || undefined}
        tabIndex={scroll.overflows ? undefined : -1}
        aria-label={`Scroll ${label} right`}
      >
        ›
      </button>
    </div>
  );
}

export function SubstanceTray({ onAdd, disabled }: SubstanceTrayProps): React.JSX.Element {
  // Gases are in the catalogue but never on the shelf — they only ever reach a
  // vessel through a delivery tube. Sorted by what the tile actually SHOWS —
  // the formula (ChemFormula's own fallback to displayName when a substance
  // has none, e.g. the indicators) — not the full name that only ever
  // appears in the hover title, so the order on screen reads alphabetical
  // rather than looking shuffled against its own labels.
  const shelfLabel = (s: Substance) => s.formula || s.displayName;
  const byShelfLabel = (a: Substance, b: Substance) => shelfLabel(a).localeCompare(shelfLabel(b));
  const liquids = SUBSTANCE_CATALOG
    .filter(s => s.physicalState === 'liquid' || s.physicalState === 'aqueous')
    .sort(byShelfLabel);
  const solids = SUBSTANCE_CATALOG
    .filter(s => s.physicalState === 'solid')
    .sort(byShelfLabel);

  return (
    <div className="chemlab-shelf-stack">
      {/* No search between the shelves. There is one in the title bar, and it
          is the better of the two: it hands you the reagent you name instead of
          picking it out of a row you then still have to read. Two fields for
          one job made the shelves narrower and the question ambiguous. */}
      <ShelfRow
        title="🧴 Liquids" label="Liquids" items={liquids} onAdd={onAdd} disabled={disabled}
      />
      <ShelfRow
        title="🧱 Solids" label="Solids" items={solids} onAdd={onAdd} disabled={disabled}
      />
    </div>
  );
}


/**
 * The same reagents, as a picker rather than a shelf.
 *
 * A shelf is the right idea on a bench: bottles in a row, reachable. It stops
 * being the right idea at 402px, where the two rows are 1,950 and 2,964 pixels
 * wide and five tiles are visible — finding sodium carbonate is eight swipes.
 *
 * So on a phone the same catalogue is searched and filtered instead of scrolled:
 * a field, the eight categories the catalogue already names, and one wrapped
 * grid, liquids before solids. Nothing about which reagents
 * exist changes; only how you reach one.
 */

export function SubstancePicker({ onAdd, disabled }: SubstanceTrayProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<SubstanceCategory | ''>('');

  const q = query.trim().toLowerCase();
  const matches = (s: Substance): boolean =>
    (!category || s.category === category)
    && (!q
      || s.displayName.toLowerCase().includes(q)
      || s.formula.toLowerCase().includes(q)
      || s.id.toLowerCase().includes(q));

  const shelfLabel = (s: Substance) => s.formula || s.displayName;
  const byShelfLabel = (a: Substance, b: Substance) => shelfLabel(a).localeCompare(shelfLabel(b));
  const pick = (states: Substance['physicalState'][]) =>
    SUBSTANCE_CATALOG.filter(s => states.includes(s.physicalState) && matches(s)).sort(byShelfLabel);

  const liquids = pick(['liquid', 'aqueous']);
  const solids = pick(['solid']);
  const total = liquids.length + solids.length;

  // Only the categories that have something to show, so a chip is never a
  // promise of an empty result.
  const present = (Object.keys(CATEGORY_LABELS) as SubstanceCategory[])
    .filter(c => SUBSTANCE_CATALOG.some(s => s.category === c && s.physicalState !== 'gas'));

  const section = (title: string, items: Substance[]) => items.length === 0 ? null : (
    <div className="chemlab-picker-section" key={title}>
      <p className="chemlab-section-title">{title}</p>
      <div className="chemlab-picker-grid">
        {items.map(s => (
          <button
            key={s.id}
            className="chemlab-shelf-item"
            onClick={e => onAdd(s.id, { x: e.clientX, y: e.clientY })}
            disabled={disabled}
            title={s.formula ? `${s.displayName} (${s.formula})` : s.displayName}
          >
            <SubstanceIcon physicalState={s.physicalState} color={s.color} form={s.form} unit={s.dose?.unit} />
            <span className="chemlab-shelf-label">
              <ChemFormula formula={s.formula} fallback={s.displayName} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="chemlab-picker">
      <div className="chemlab-picker-head">
        <span className="chemlab-shelf-search-icon" aria-hidden="true">🔍</span>
        <input
          className="chemlab-picker-search"
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by name or formula"
          aria-label="Search reagents"
          autoComplete="off"
        />
      </div>
      <div className="chemlab-picker-chips" role="group" aria-label="Filter by category">
        <button
          className="chemlab-chip"
          aria-pressed={category === ''}
          onClick={() => setCategory('')}
        >All</button>
        {present.map(c => (
          <button
            key={c}
            className="chemlab-chip"
            aria-pressed={category === c}
            onClick={() => setCategory(prev => (prev === c ? '' : c))}
          >{CATEGORY_LABELS[c]}</button>
        ))}
      </div>
      <div className="chemlab-picker-body">
        {total === 0
          ? <p className="chemlab-picker-empty">Nothing matches. Clear the search or pick another category.</p>
          : <>{section('🧴 Liquids & solutions', liquids)}{section('🧱 Solids', solids)}</>}
      </div>
    </div>
  );
}
